import {
  AimOutlined,
  BarChartOutlined,
  CloudServerOutlined,
  // DashboardOutlined,
  DatabaseOutlined,
  // FileTextOutlined,
  // MonitorOutlined,
  VideoCameraOutlined
} from "@ant-design/icons";
import { useEffect, useRef, useState } from "react";
import KuikaSvg from "../../../../ui-kit/kuika-svg/kuika-svg";
import KuikaTooltip from "../../../../ui-kit/kuika-tooltip/kuika-tooltip";
import { useDnD } from "./dnd-context";
import { NODE_TOOLTIPS } from "./nodes/nodesInfoContent";
import styles from "./FloatingNodePalette.module.css";

interface NodeDefinition {
  type: string;
  label: string;
  icon: JSX.Element;
  color: string;
}

// Node types use camelCase to match backend EnumMember values
const nodeCategories: Record<string, { icon: JSX.Element; color: string; nodes: NodeDefinition[] }> = {
  "Input Sources": {
    icon: <VideoCameraOutlined />,
    color: "#3b82f6",
    nodes: [
      {
        type: "videoInput",
        label: "Video Input",
        icon: <VideoCameraOutlined />,
        color: "#3b82f6"
      }
    ]
  },
  "AI Processing": {
    icon: <AimOutlined />,
    color: "#10b981",
    nodes: [
      {
        type: "detector",
        label: "Detector",
        icon: <AimOutlined />,
        color: "#10b981"
      },
      {
        type: "tracker",
        label: "Tracker",
        icon: <AimOutlined />,
        color: "#10b981"
      },
      {
        type: "segmentation",
        label: "Segmentation",
        icon: <AimOutlined />,
        color: "#10b981"
      }
    ]
  },
  Analytics: {
    icon: <BarChartOutlined />,
    color: "#f59e0b",
    nodes: [
      {
        type: "analyticsProcessor",
        label: "Analytics Processor",
        icon: <BarChartOutlined />,
        color: "#f59e0b"
      }
      // {
      //   type: "metricsCalculator",
      //   label: "Metrics Calculator",
      //   icon: <LineChartOutlined />,
      //   color: "#f59e0b"
      // }
    ]
  },
  // Outputs: {
  //   icon: <DashboardOutlined />,
  //   color: "#cf3112",
  //   nodes: [
  //     {
  //       type: "dashboardOutput",
  //       label: "Dashboard Output",
  //       icon: <MonitorOutlined />,
  //       color: "#cf3112"
  //     },
  //     {
  //       type: "reportGenerator",
  //       label: "Report Generator",
  //       icon: <FileTextOutlined />,
  //       color: "#cf3112"
  //     }
  //   ]
  // },
  Services: {
    icon: <CloudServerOutlined />,
    color: "var(--purple-5)",
    nodes: [
      {
        type: "databaseService",
        label: "Database Service",
        icon: <DatabaseOutlined />,
        color: "var(--purple-5)"
      }
      // {
      //   type: "apiGateway",
      //   label: "API Gateway",
      //   icon: <CloudServerOutlined />,
      //   color: "var(--purple-5)"
      // }
    ]
  }
};

const FloatingNodePalette = () => {
  const [expandedCategory, setExpandedCategory] = useState<string | null>(null);
  const { setType } = useDnD();
  const paletteRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const closeTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const onDragStart = (event: React.DragEvent, nodeType: string) => {
    setType(nodeType);
    event.dataTransfer.effectAllowed = "move";
  };

  const onDragEnd = () => {
    setExpandedCategory(null);
  };

  const handleCategoryHover = (category: string) => {
    // Clear any pending close timeout when hovering over a category
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
    setExpandedCategory(category);
  };

  const handlePaletteLeave = () => {
    // Set a 2 second delay before closing
    closeTimeoutRef.current = setTimeout(() => {
      setExpandedCategory(null);
    }, 500);
  };

  const toggleCategory = (category: string, event: React.MouseEvent) => {
    event.stopPropagation();
    setExpandedCategory(expandedCategory === category ? null : category);
  };

  // Click outside to close
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (expandedCategory && paletteRef.current && !paletteRef.current.contains(event.target as Node)) {
        setExpandedCategory(null);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      // Clean up timeout on unmount
      if (closeTimeoutRef.current) {
        clearTimeout(closeTimeoutRef.current);
      }
    };
  }, [expandedCategory]);

  return (
    <div className={styles.floatingPalette} ref={paletteRef} onMouseLeave={handlePaletteLeave}>
      <div className={styles.categoryIcons}>
        {Object.entries(nodeCategories).map(([category, { icon, color }]) => (
          <button
            key={category}
            className={`${styles.categoryIcon} ${expandedCategory === category ? styles.categoryIconActive : ""}`}
            onMouseEnter={() => handleCategoryHover(category)}
            title={category}
            style={
              expandedCategory === category
                ? {
                    backgroundColor: color,
                    borderColor: color,
                    color: "#ffffff"
                  }
                : {
                    color
                  }
            }
          >
            {icon}
          </button>
        ))}
      </div>

      {expandedCategory && (
        <div className={`${styles.nodePanel} ${styles.nodePanelVisible}`} ref={panelRef}>
          <div className={styles.nodeList}>
            {nodeCategories[expandedCategory].nodes.map((node) => (
              <div
                key={node.type}
                className={styles.nodeItem}
                draggable
                onDragStart={(e) => onDragStart(e, node.type)}
                onDragEnd={onDragEnd}
                style={{ borderColor: node.color }}
              >
                <span className={styles.nodeIcon} style={{ color: node.color }}>
                  {node.icon}
                </span>
                <div className={styles.nodeLabel}>
                  {node.label}
                  <KuikaTooltip
                    position="right"
                    content={<div className={styles.nodeTooltipContent}>{NODE_TOOLTIPS[node.type]?.text}</div>}
                  >
                    <span className={styles.paletteInfoIcon}>
                      <KuikaSvg name="info-circle" width={14} height={14} />
                    </span>
                  </KuikaTooltip>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default FloatingNodePalette;
